import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { 
  GET_WHERE_TO_GO_CATEGORY, 
  CREATE_WHERE_TO_GO_CATEGORY, 
  UPDATE_WHERE_TO_GO_CATEGORY 
} from '../graphql/whereToGo';
import { toast } from 'react-hot-toast';

// Define the form validation schema
const categorySchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name is too long'),
  description: z.string().max(500, 'Description is too long').optional(),
  isActive: z.boolean().default(true)
});

export default function WhereToGoCategory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const isEditMode = !!id;
  
  const { loading: loadingCategory, data } = useQuery(GET_WHERE_TO_GO_CATEGORY, {
    variables: { id },
    skip: !isEditMode
  });

  const [createCategory, { loading: creating }] = useMutation(CREATE_WHERE_TO_GO_CATEGORY, {
    onCompleted: () => {
      toast.success('Category created successfully!');
      navigate('/dashboard/where-to-go');
    },
    onError: (error) => {
      setError(error.message);
      toast.error(`Error creating category: ${error.message}`);
    }
  });

  const [updateCategory, { loading: updating }] = useMutation(UPDATE_WHERE_TO_GO_CATEGORY, {
    onCompleted: () => {
      toast.success('Category updated successfully!');
      navigate('/dashboard/where-to-go');
    },
    onError: (error) => {
      setError(error.message);
      toast.error(`Error updating category: ${error.message}`);
    }
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: data?.whereToGoCategory?.name || '',
      description: data?.whereToGoCategory?.description || '',
      isActive: data?.whereToGoCategory?.isActive ?? true
    }
  });

  // Update form when data is loaded
  React.useEffect(() => {
    if (data?.whereToGoCategory) {
      reset({
        name: data.whereToGoCategory.name,
        description: data.whereToGoCategory.description || '',
        isActive: data.whereToGoCategory.isActive
      });
    }
  }, [data, reset]);

  const onSubmit = async (formData) => {
    try {
      setError(null);
      
      if (isEditMode) {
        await updateCategory({
          variables: {
            input: {
              id,
              ...formData
            }
          }
        });
      } else {
        await createCategory({
          variables: {
            input: formData
          }
        });
      }
    } catch (err) {
      console.error('Category form error:', err);
    }
  };

  if (isEditMode && loadingCategory) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">
            {isEditMode ? 'Edit Category' : 'Create New Category'}
          </h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          
          <div className="space-y-4">
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Category Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                {...register('name')}
                className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                aria-invalid={errors.name ? 'true' : 'false'}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600" role="alert">{errors.name.message}</p>
              )}
            </div>

            {/* Description Field */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                rows={4}
                {...register('description')}
                className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600" role="alert">{errors.description.message}</p>
              )}
            </div>

            {/* Active Status */}
            <div className="flex items-center">
              <input
                id="isActive"
                type="checkbox"
                {...register('isActive')}
                className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                Active (show in dropdown filters)
              </label>
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={creating || updating || isSubmitting}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {(creating || updating || isSubmitting) && (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {creating || updating || isSubmitting ? 'Saving...' : isEditMode ? 'Update Category' : 'Create Category'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 